import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  MultiFactorResolver,
  TotpMultiFactorGenerator,
  browserSessionPersistence,
  getMultiFactorResolver,
  setPersistence,
  signInWithEmailAndPassword,
} from "firebase/auth";
import { auth } from "../firebase";
import AuthLayout from "../components/auth/AuthLayout";
import OtpCodeInput from "../components/auth/OtpCodeInput";

// Server-enforced MFA should always be honored when returned by Firebase.
// This flag only controls proactive enrollment checks after successful sign-in.
const ENFORCE_MFA_ENROLLMENT = process.env.REACT_APP_REQUIRE_MFA !== "false";

const Form = styled.form`
  display: grid;
  gap: 0.86rem;
`;

const Field = styled.div`
  display: grid;
  gap: 0.34rem;
`;

const Label = styled.label`
  color: #475569;
  font-size: 0.79rem;
  font-weight: 500;
`;

const TextInput = styled.input`
  width: 100%;
  height: 2.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  padding: 0 0.8rem;
  font-size: 0.92rem;
  color: #0f172a;
  transition: border-color 150ms ease-in-out, box-shadow 150ms ease-in-out;

  &:focus-visible {
    outline: 0;
    border-color: #64748b;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.24);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  border: 1px solid #fecaca;
  background: #fff7f7;
  color: #b91c1c;
  border-radius: 10px;
  padding: 0.56rem 0.65rem;
  font-size: 0.8rem;
`;

const InfoMessage = styled.p`
  margin: -0.15rem 0 0.2rem;
  color: #64748b;
  font-size: 0.84rem;
  line-height: 1.5;
`;

const PrimaryButton = styled.button`
  height: 2.9rem;
  border: 1px solid #2f435f;
  border-radius: 10px;
  background: #324966;
  color: #ffffff;
  font-size: 0.92rem;
  font-weight: 650;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  transition: transform 150ms ease-in-out, background-color 150ms ease-in-out, border-color 150ms ease-in-out, box-shadow 150ms ease-in-out;
  box-shadow: inset 0 1px rgba(255, 255, 255, 0.14), 0 6px 16px rgba(15, 23, 42, 0.16);

  &:hover {
    transform: translateY(-1px);
    background: #2a3d57;
    border-color: #2a3d57;
    box-shadow: inset 0 1px rgba(255, 255, 255, 0.12), 0 10px 20px rgba(15, 23, 42, 0.18);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.24);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const SecondaryButton = styled.button`
  height: 2.6rem;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  background: #f8fafc;
  color: #334155;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out;

  &:hover {
    background: #f1f5f9;
    border-color: #9ca3af;
  }

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 3px rgba(100, 116, 139, 0.24);
  }
`;

const ButtonStack = styled.div`
  display: grid;
  gap: 0.58rem;
`;

const SpinnerIcon = styled(Loader2)`
  animation: spin 0.9s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
  const [resolver, setResolver] = useState<MultiFactorResolver | null>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);

  const navigate = useNavigate();

  const handleLogin = useCallback(async () => {
    if (isLoading) return;

    if (!email.trim() || !password) {
      setError("Enter your email and password.");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      await setPersistence(auth, browserSessionPersistence);
      const userCredential = await signInWithEmailAndPassword(auth, email.trim(), password);
      const user = userCredential.user;

      if (ENFORCE_MFA_ENROLLMENT) {
        const enrolledFactors = (user as any).multiFactor?.enrolledFactors || [];
        if (enrolledFactors.length === 0) {
          navigate("/enroll-mfa");
          return;
        }
      }
      
      navigate("/auto-claims");
    } catch (signInError: any) {
      if (signInError.code === "auth/multi-factor-auth-required") {
        setResolver(getMultiFactorResolver(auth, signInError));
        setOtp(new Array(6).fill(""));
        setError("");
      } else {
        setError("Login failed. Check your credentials and try again.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [email, isLoading, navigate, password]);

  const handleMfaVerify = useCallback(
    async (overrideCode?: string) => {
      if (!resolver || isVerifying) return;

      const code = (overrideCode || otp.join("")).trim();
      if (code.length !== 6) {
        setError("Please enter the complete 6-digit code.");
        return;
      }

      setIsVerifying(true);
      setError("");

      try {
        const hint = resolver.hints.find(
          (factorHint) => factorHint.factorId === TotpMultiFactorGenerator.FACTOR_ID
        );

        if (!hint) {
          setError("No authenticator factor found for this account.");
          return;
        }

        const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code);
        await resolver.resolveSignIn(assertion);
        navigate("/auto-claims");
      } catch {
        setError("Invalid code. Please try again.");
      } finally {
        setIsVerifying(false);
      }
    },
    [navigate, otp, resolver, isVerifying]
  );

  const onLoginSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleLogin();
  };

  const onMfaSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void handleMfaVerify();
  };

  return (
    <AuthLayout
      cardTitle={resolver ? "Verify your identity" : "Sign in to MedhaCode"}
      cardSubtitle={
        resolver
          ? "Enter the 6-digit code from your authenticator app to continue securely."
          : "Access your secure revenue operations workspace."
      }
    >
      {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

      {!resolver ? (
        <Form onSubmit={onLoginSubmit} noValidate>
          <Field>
            <Label htmlFor="login-email">Email</Label>
            <TextInput
              id="login-email"
              name="email"
              type="email"
              autoComplete="email"
              placeholder="name@hospital.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              required
            />
          </Field>

          <Field>
            <Label htmlFor="login-password">Password</Label>
            <TextInput
              id="login-password"
              name="password"
              type="password"
              autoComplete="current-password"
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
              required
            />
          </Field>

          <PrimaryButton type="submit" disabled={isLoading}>
            {isLoading ? <SpinnerIcon size={16} /> : null}
            {isLoading ? "Signing in..." : "Sign in"}
          </PrimaryButton>
        </Form>
      ) : (
        <Form onSubmit={onMfaSubmit} noValidate>
          <InfoMessage>
            Open your authenticator app and enter the current 6-digit code.
          </InfoMessage>

          <OtpCodeInput
            value={otp}
            onChange={setOtp}
            onSubmit={(code) => {
              if (!isVerifying) {
                void handleMfaVerify(code);
              }
            }}
            disabled={isVerifying}
            autoFocus
          />

          <ButtonStack>
            <PrimaryButton type="submit" disabled={isVerifying}>
              {isVerifying ? <SpinnerIcon size={16} /> : null}
              {isVerifying ? "Verifying..." : "Verify & Continue"}
            </PrimaryButton>
            <SecondaryButton
              type="button"
              onClick={() => {
                setResolver(null);
                setOtp(new Array(6).fill(""));
                setError("");
              }}
              disabled={isVerifying}
            >
              Back to sign in
            </SecondaryButton>
          </ButtonStack>
        </Form>
      )}
    </AuthLayout>
  );
}
